<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { useAuthUserStore } from '../Store/AuthUserStore.js'
import { useSubscriptionStore } from '../Store/SubscriptionStore.js'
import { MODULES } from '../Constants/modules.js'

const router = useRouter()
const authUserStore = useAuthUserStore()
const subscriptionStore = useSubscriptionStore()

const MIN_LENGTH = 2
const DEBOUNCE_MS = 140
const RECENT_KEY = 'global-search-recent'
const RECENT_MAX = 6
// Une recherche refaite dans les 30 s est resservie sans appel ; au-delà, les données ont pu changer
const CACHE_TTL_MS = 30000
const isMac = /Mac|iPhone|iPad/.test(navigator.platform)

// Apparence de chaque type de résultat
const TYPES = {
    pages: { icon: 'mdi-compass-outline', color: 'dark' },
    client_requests: { icon: 'mdi-car-info', color: 'primary' },
    clients: { icon: 'mdi-account-outline', color: 'info' },
    tickets: { icon: 'mdi-ticket-confirmation-outline', color: 'warning' },
    quotes: { icon: 'mdi-file-document-edit-outline', color: 'success' },
    invoices: { icon: 'mdi-receipt-text-outline', color: 'success' },
    vehicle_engines: { icon: 'mdi-engine-outline', color: 'danger' },
    help_articles: { icon: 'mdi-lifebuoy', color: 'secondary' },
    news: { icon: 'mdi-newspaper-variant-outline', color: 'secondary' },
    users: { icon: 'mdi-account-tie-outline', color: 'primary' },
    centers: { icon: 'mdi-storefront-outline', color: 'info' },
    zoho_invoices: { icon: 'mdi-receipt', color: 'warning' },
    products: { icon: 'mdi-package-variant-closed', color: 'dark' },
}

const STAFF_SIDE = ['admin', 'back_office']

// Pages de l'app, filtrées par rôle et par modules du plan : navigation instantanée, sans appel serveur
const PAGES = [
    { label: 'Tableau de bord', route: 'dashboard', icon: 'mdi-view-dashboard-outline', keywords: 'accueil dashboard' },
    { label: 'Mon profil', route: 'users.profile', icon: 'mdi-account-circle-outline', keywords: 'compte mot de passe' },
    { label: 'Demandes clients', route: 'client-requests.list', icon: 'mdi-car-info', module: MODULES.CLIENT_REQUESTS, keywords: 'leads' },
    { label: 'Agenda RDV', route: 'client-requests.agenda', icon: 'mdi-calendar-month-outline', module: MODULES.CLIENT_REQUESTS, keywords: 'rendez-vous calendrier planning' },
    { label: 'Autres demandes', route: 'client-request-others.list', icon: 'mdi-inbox-multiple-outline', module: MODULES.CLIENT_REQUESTS, keywords: 'jobs franchise' },
    { label: "Demandes d'infos", route: 'information-requests.list', icon: 'mdi-information-outline', module: MODULES.INFORMATION_REQUESTS },
    { label: 'Contacts', route: 'clients.list', icon: 'mdi-account-multiple-outline', module: MODULES.CLIENTS, keywords: 'clients' },
    { label: "Liste d'appels", route: 'calls.list', icon: 'mdi-phone-outline', module: MODULES.CALLS, keywords: 'appels ringover téléphone', needsCalls: true },
    { label: 'Devis', route: 'quotes.list', icon: 'mdi-file-document-edit-outline', module: MODULES.QUOTES },
    { label: 'Factures', route: 'invoices.list', icon: 'mdi-receipt-text-outline', module: MODULES.INVOICES },
    { label: 'Factures Zoho', route: 'invoices-zoho.list', icon: 'mdi-receipt', module: MODULES.INVOICES_ZOHO },
    { label: 'Produits & services', route: 'products.list', icon: 'mdi-package-variant-closed', module: MODULES.PRODUCTS },
    { label: 'Reprogrammations', route: 'chiptuning.list', icon: 'mdi-engine-outline', module: MODULES.CHIPTUNING, keywords: 'chiptuning véhicules moteurs' },
    { label: 'Tickets', route: 'tickets.list', icon: 'mdi-ticket-confirmation-outline', module: MODULES.TICKETS, keywords: 'support' },
    { label: "Centre d'aide", route: 'help-articles.list', icon: 'mdi-lifebuoy', module: MODULES.HELP_CENTER, keywords: 'aide fiches documentation' },
    { label: 'Actualités', route: 'news.list', icon: 'mdi-newspaper-variant-outline', module: MODULES.NEWS, keywords: 'news' },
    { label: 'Événements', route: 'events.list', icon: 'mdi-calendar-star', module: MODULES.EVENTS },
    { label: 'Formulaires', route: 'surveys.list', icon: 'mdi-poll', module: MODULES.SURVEYS },
    { label: 'Bibliothèque médias', route: 'social-network-media.list', icon: 'mdi-image-multiple-outline', module: MODULES.MEDIA_LIBRARY, keywords: 'photos réseaux sociaux' },
    { label: 'Messages prédéfinis', route: 'default-messages.list', icon: 'mdi-message-text-outline', roles: STAFF_SIDE },
    { label: 'Statistiques', route: 'statistics.index', icon: 'mdi-chart-box-outline', roles: [...STAFF_SIDE, 'manager'], keywords: 'stats chiffres' },
    { label: 'Centres', route: 'centers.list', icon: 'mdi-storefront-outline', roles: STAFF_SIDE, keywords: 'agences' },
    { label: 'Utilisateurs', route: 'users.list', icon: 'mdi-account-tie-outline', roles: STAFF_SIDE },
    { label: "Bandeaux d'annonce", route: 'announcements.list', icon: 'mdi-bullhorn-outline', roles: STAFF_SIDE },
]

const isOpen = ref(false)
const query = ref('')
const groups = ref([])
const loading = ref(false)
const activeIndex = ref(0)
const recent = ref(readRecent())
const input = ref(null)
const list = ref(null)

const cache = new Map()
let debounceTimer = null
let controller = null

const trimmedQuery = computed(() => query.value.trim())

const normalize = (text) => (text ?? '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

const availablePages = computed(() => {
    const user = authUserStore.authUser ?? {}

    return PAGES.filter((page) => (!page.roles || page.roles.includes(user.role))
        && (!page.module || subscriptionStore.hasModuleAccess(page.module))
        && (!page.needsCalls || user.has_calls !== false)
        && router.hasRoute(page.route))
})

const pageHits = computed(() => {
    const needle = normalize(trimmedQuery.value)

    if (!needle) {
        return []
    }

    return availablePages.value
        .filter((page) => normalize(`${page.label} ${page.keywords ?? ''}`).includes(needle))
        .slice(0, 4)
        .map((page) => ({ type: 'pages', id: page.route, title: page.label, icon: page.icon, route: { name: page.route } }))
})

// Groupes affichés : pages trouvées, puis résultats serveur ; sans saisie, les derniers ouverts
const displayedGroups = computed(() => {
    if (!trimmedQuery.value) {
        return recent.value.length ? [{ type: 'recent', label: 'Récemment ouverts', hits: recent.value }] : []
    }

    const pages = pageHits.value.length ? [{ type: 'pages', label: 'Aller à', hits: pageHits.value }] : []

    return [...pages, ...groups.value]
})

const flatHits = computed(() => displayedGroups.value.flatMap((group) => group.hits))

const isSearching = computed(() => trimmedQuery.value.length >= MIN_LENGTH)
const hasNoResult = computed(() => isSearching.value && !loading.value && flatHits.value.length === 0)

function open() {
    isOpen.value = true
    recent.value = readRecent()
    activeIndex.value = 0
    nextTick(() => input.value?.select())
}

function close() {
    isOpen.value = false
}

function onGlobalKeydown(event) {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        isOpen.value ? close() : open()
    }
}

function onInputKeydown(event) {
    const count = flatHits.value.length

    if (event.key === 'ArrowDown' && count) {
        event.preventDefault()
        activeIndex.value = (activeIndex.value + 1) % count
        scrollActiveIntoView()
    } else if (event.key === 'ArrowUp' && count) {
        event.preventDefault()
        activeIndex.value = (activeIndex.value - 1 + count) % count
        scrollActiveIntoView()
    } else if (event.key === 'Enter' && count) {
        event.preventDefault()
        select(flatHits.value[activeIndex.value])
    } else if (event.key === 'Escape') {
        event.preventDefault()
        close()
    }
}

function scrollActiveIntoView() {
    nextTick(() => list.value?.querySelector('.gs-item.is-active')?.scrollIntoView({ block: 'nearest' }))
}

function cachedResults(term) {
    const entry = cache.get(term)

    return entry && Date.now() - entry.at < CACHE_TTL_MS ? entry.groups : null
}

async function fetchResults(term) {
    if (cachedResults(term)) {
        groups.value = cachedResults(term)
        loading.value = false
        return
    }

    controller?.abort()
    controller = new AbortController()
    loading.value = true

    try {
        const response = await axios.get('/api/search/global', { params: { query: term }, signal: controller.signal })
        cache.set(term, { at: Date.now(), groups: response.data.data })

        // Une réponse arrivée après une nouvelle saisie est ignorée
        if (term === trimmedQuery.value) {
            groups.value = response.data.data
        }
    } catch (error) {
        if (!axios.isCancel(error)) {
            groups.value = []
        }
    } finally {
        if (term === trimmedQuery.value) {
            loading.value = false
        }
    }
}

watch(trimmedQuery, (term) => {
    activeIndex.value = 0
    clearTimeout(debounceTimer)

    if (term.length < MIN_LENGTH) {
        controller?.abort()
        groups.value = []
        loading.value = false
        return
    }

    loading.value = true
    debounceTimer = setTimeout(() => fetchResults(term), cachedResults(term) ? 0 : DEBOUNCE_MS)
})

function select(hit) {
    if (!hit) {
        return
    }

    rememberRecent(hit)
    close()
    router.push(hit.route)
}

function seeAll(group) {
    close()
    router.push({ name: group.list_route, query: { q: trimmedQuery.value } })
}

function readRecent() {
    try {
        return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]')
    } catch {
        return []
    }
}

function rememberRecent(hit) {
    const entry = { type: hit.type, id: hit.id, title: hit.title, subtitle: hit.subtitle ?? null, icon: hit.icon ?? null, route: hit.route }
    const others = recent.value.filter((item) => !(item.type === entry.type && item.id === entry.id))
    recent.value = [entry, ...others].slice(0, RECENT_MAX)

    try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(recent.value))
    } catch {
        // Stockage indisponible (navigation privée) : on garde la liste en mémoire
    }
}

function clearRecent() {
    recent.value = []

    try {
        localStorage.removeItem(RECENT_KEY)
    } catch {
        // Stockage indisponible
    }
}

const escapeHtml = (text) => text.replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char])

// Surligne les mots saisis, sans tenir compte des accents ni de la casse (échappement caractère par caractère)
function highlight(text) {
    if (!text) {
        return ''
    }

    const raw = String(text)
    const words = trimmedQuery.value.split(/\s+/).filter((word) => word.length >= 2).map(normalize)
    const source = normalize(raw)
    const marks = new Array(raw.length).fill(false)

    // La normalisation ne change la longueur que pour des caractères rares : sans surlignage dans ce cas
    if (source.length === raw.length) {
        words.forEach((needle) => {
            for (let from = source.indexOf(needle); from !== -1; from = source.indexOf(needle, from + needle.length)) {
                marks.fill(true, from, from + needle.length)
            }
        })
    }

    let html = ''
    let inside = false
    for (let index = 0; index < raw.length; index++) {
        if (marks[index] !== inside) {
            html += marks[index] ? '<mark>' : '</mark>'
            inside = marks[index]
        }
        html += escapeHtml(raw[index])
    }

    return inside ? html + '</mark>' : html
}

const typeOf = (hit) => TYPES[hit.type] ?? TYPES.pages
const iconOf = (hit) => hit.icon ?? typeOf(hit).icon
const indexOf = (hit) => flatHits.value.indexOf(hit)

onMounted(() => window.addEventListener('keydown', onGlobalKeydown))
onBeforeUnmount(() => {
    window.removeEventListener('keydown', onGlobalKeydown)
    controller?.abort()
    clearTimeout(debounceTimer)
})

// Pas de défilement de la page derrière la fenêtre
watch(isOpen, (value) => document.body.classList.toggle('gs-no-scroll', value))

defineExpose({ open })
</script>

<template>
    <!-- Déclencheur de la barre du haut -->
    <button type="button" class="gs-trigger d-none d-md-flex" @click="open" title="Recherche globale">
        <i class="ri-search-line"></i>
        <span class="gs-trigger-label">Rechercher…</span>
        <kbd>{{ isMac ? '⌘' : 'Ctrl' }} K</kbd>
    </button>
    <button type="button" class="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle shadow-none d-md-none" @click="open" title="Recherche globale">
        <i class="ri-search-line fs-20"></i>
    </button>

    <Teleport to="body">
        <Transition name="gs-fade">
            <div class="gs-backdrop" v-if="isOpen" @mousedown.self="close">
                <div class="gs-panel" role="dialog" aria-modal="true" aria-label="Recherche globale">
                    <div class="gs-input-wrap">
                        <i class="ri-search-line gs-input-icon"></i>
                        <input
                            ref="input"
                            v-model="query"
                            type="text"
                            class="gs-input"
                            placeholder="Contact, demande, plaque, VIN, ticket, véhicule, page…"
                            autocomplete="off"
                            spellcheck="false"
                            @keydown="onInputKeydown"
                        >
                        <button type="button" class="gs-clear" v-if="query" @click="query = ''; input?.focus()" title="Effacer">
                            <i class="ri-close-circle-fill"></i>
                        </button>
                        <kbd class="gs-esc" @click="close">Échap</kbd>
                        <div class="gs-progress" v-if="loading"></div>
                    </div>

                    <div class="gs-results" ref="list">
                        <template v-for="group in displayedGroups" :key="group.type">
                            <div class="gs-group-header">
                                <span>{{ group.label }}</span>
                                <button type="button" class="gs-group-action" v-if="group.type === 'recent'" @click="clearRecent">Effacer</button>
                                <button type="button" class="gs-group-action" v-else-if="group.list_route && group.hits.length >= 5" @click="seeAll(group)">
                                    Voir tous les résultats <i class="ri-arrow-right-line"></i>
                                </button>
                            </div>
                            <div
                                v-for="hit in group.hits"
                                :key="hit.type + '-' + hit.id"
                                class="gs-item"
                                :class="{ 'is-active': indexOf(hit) === activeIndex }"
                                @mousemove="activeIndex = indexOf(hit)"
                                @click="select(hit)"
                            >
                                <span class="gs-item-icon" :class="`bg-${typeOf(hit).color}-subtle text-${typeOf(hit).color}`">
                                    <i class="mdi" :class="iconOf(hit)"></i>
                                </span>
                                <span class="gs-item-text">
                                    <span class="gs-item-title" v-html="highlight(hit.title)"></span>
                                    <span class="gs-item-subtitle" v-if="hit.subtitle || hit.meta">
                                        <span v-html="highlight(hit.subtitle)"></span>
                                        <span class="gs-item-meta" v-if="hit.meta">{{ hit.meta }}</span>
                                    </span>
                                </span>
                                <span class="badge gs-item-badge" v-if="hit.badge" :class="`bg-${hit.badge_color ?? 'light'}-subtle text-${hit.badge_color ?? 'body'}`">{{ hit.badge }}</span>
                                <i class="ri-corner-down-left-line gs-item-enter"></i>
                            </div>
                        </template>

                        <!-- États vides -->
                        <div class="gs-empty" v-if="hasNoResult">
                            <i class="ri-search-eye-line"></i>
                            <p class="mb-0">Aucun résultat pour « {{ trimmedQuery }} »</p>
                            <span>Essayez un nom, un téléphone, une plaque ou un numéro de devis.</span>
                        </div>
                        <div class="gs-empty" v-else-if="!trimmedQuery && !recent.length">
                            <i class="ri-flashlight-line"></i>
                            <p class="mb-0">Tout trouver, depuis n'importe où</p>
                            <span>Contacts, demandes (plaque, VIN), tickets, devis, factures, véhicules, fiches d'aide et pages.</span>
                        </div>
                        <div class="gs-empty is-compact" v-else-if="trimmedQuery && !isSearching && !flatHits.length">
                            <span>Encore un caractère…</span>
                        </div>
                    </div>

                    <div class="gs-footer">
                        <span><kbd>↑</kbd><kbd>↓</kbd> naviguer</span>
                        <span><kbd>↵</kbd> ouvrir</span>
                        <span><kbd>Échap</kbd> fermer</span>
                        <span class="ms-auto d-none d-sm-inline"><kbd>{{ isMac ? '⌘' : 'Ctrl' }}</kbd><kbd>K</kbd> depuis n'importe quelle page</span>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
/* Couleur d'accent : celle de la marque (--brand-accent, feuilles resources/css/<Marque>/brand.css), sinon celle du thème */
/* Déclencheur : pastille discrète dans la barre du haut */
.gs-trigger {
    align-items: center;
    gap: 0.5rem;
    min-width: 15rem;
    height: 2.4rem;
    margin-right: 0.5rem;
    padding: 0 0.5rem 0 0.85rem;
    /* Même fond que le bloc utilisateur voisin ; bordure seulement au survol */
    border: 1px solid transparent;
    border-radius: 999px;
    background-color: var(--vz-topbar-user-bg);
    color: var(--vz-secondary-color);
    font-size: 0.875rem;
    transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}

.gs-trigger:hover {
    border-color: var(--vz-border-color);
    color: var(--vz-body-color);
}

.gs-trigger-label {
    flex-grow: 1;
    text-align: left;
}

kbd {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.4rem;
    height: 1.4rem;
    padding: 0 0.35rem;
    border: 1px solid var(--vz-border-color);
    border-bottom-width: 2px;
    border-radius: 0.35rem;
    background-color: var(--vz-secondary-bg);
    color: var(--vz-secondary-color);
    font-family: inherit;
    font-size: 0.7rem;
    font-weight: 600;
    line-height: 1;
}

/* Fenêtre */
.gs-backdrop {
    position: fixed;
    inset: 0;
    z-index: 2000;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 12vh 1rem 1rem;
    background-color: rgba(15, 23, 42, 0.45);
    backdrop-filter: blur(3px);
}

.gs-panel {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 42rem;
    max-height: 76vh;
    overflow: hidden;
    border: 1px solid var(--vz-border-color);
    border-radius: 1rem;
    background-color: var(--vz-secondary-bg);
    box-shadow: 0 1.5rem 4rem rgba(15, 23, 42, 0.3);
}

.gs-input-wrap {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0 1.1rem;
    border-bottom: 1px solid var(--vz-border-color);
}

.gs-input-icon {
    font-size: 1.35rem;
    color: var(--brand-accent, var(--vz-primary));
}

.gs-input {
    flex-grow: 1;
    min-width: 0;
    height: 3.6rem;
    border: 0;
    outline: 0;
    background: transparent;
    color: var(--vz-body-color);
    font-size: 1.05rem;
}

.gs-input::placeholder {
    color: var(--vz-secondary-color);
    opacity: 0.8;
}

.gs-clear {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.4rem;
    height: 1.4rem;
    padding: 0;
    border: 0;
    background: none;
    color: var(--vz-secondary-color);
    font-size: 1.1rem;
}

.gs-clear i,
.gs-group-action i {
    display: block;
    line-height: 1;
}

.gs-esc {
    cursor: pointer;
}

/* Chargement : fin liseré animé sous le champ, sans décaler les résultats */
.gs-progress {
    position: absolute;
    right: 0;
    bottom: -1px;
    left: 0;
    height: 2px;
    overflow: hidden;
}

.gs-progress::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 35%;
    background: linear-gradient(90deg, transparent, var(--brand-accent, var(--vz-primary)), transparent);
    animation: gs-progress 0.9s ease-in-out infinite;
}

@keyframes gs-progress {
    from { left: -35%; }
    to { left: 100%; }
}

.gs-results {
    flex-grow: 1;
    overflow-y: auto;
    padding: 0.35rem 0.6rem 0.6rem;
    overscroll-behavior: contain;
}

.gs-group-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.8rem 0.6rem 0.35rem;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--vz-secondary-color);
}

.gs-group-action {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0;
    border: 0;
    background: none;
    color: var(--brand-accent, var(--vz-secondary));
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: normal;
    text-transform: none;
}

.gs-group-action:hover {
    text-decoration: underline;
}

.gs-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0.6rem;
    border-radius: 0.6rem;
    cursor: pointer;
}

.gs-item.is-active {
    /* Fond neutre ; seul le liseré de gauche prend la couleur de la marque */
    background-color: var(--vz-light);
}

.gs-item.is-active::before {
    content: '';
    position: absolute;
    top: 0.55rem;
    bottom: 0.55rem;
    left: 0;
    width: 3px;
    border-radius: 3px;
    background-color: var(--brand-accent, var(--vz-primary));
}

.gs-item-icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.6rem;
    font-size: 1.15rem;
}

.gs-item-text {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-width: 0;
    line-height: 1.3;
}

.gs-item-title,
.gs-item-subtitle {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.gs-item-title {
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--vz-body-color);
}

.gs-item-subtitle {
    font-size: 0.78rem;
    color: var(--vz-secondary-color);
}

.gs-item-meta::before {
    content: '·';
    margin: 0 0.35rem;
}

.gs-item-badge {
    flex-shrink: 0;
    font-weight: 500;
}

.gs-item-enter {
    flex-shrink: 0;
    color: var(--vz-secondary-color);
    opacity: 0;
    transition: opacity 0.1s ease;
}

.gs-item.is-active .gs-item-enter {
    opacity: 1;
}

.gs-item :deep(mark) {
    padding: 0;
    border-radius: 2px;
    background-color: rgba(var(--vz-warning-rgb), 0.3);
    color: inherit;
}

.gs-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.35rem;
    padding: 2.5rem 1.5rem;
    text-align: center;
    color: var(--vz-secondary-color);
}

.gs-empty i {
    font-size: 2rem;
    color: var(--brand-accent, var(--vz-primary));
    opacity: 0.7;
}

.gs-empty p {
    font-weight: 500;
    color: var(--vz-body-color);
}

.gs-empty span {
    max-width: 26rem;
    font-size: 0.8rem;
}

.gs-empty.is-compact {
    padding: 1.25rem;
}

.gs-footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem;
    padding: 0.6rem 1.1rem;
    border-top: 1px solid var(--vz-border-color);
    background-color: var(--vz-light);
    font-size: 0.75rem;
    color: var(--vz-secondary-color);
}

.gs-footer kbd {
    margin-right: 0.2rem;
}

/* Apparition : fondu du fond, léger zoom de la fenêtre */
.gs-fade-enter-active,
.gs-fade-leave-active {
    transition: opacity 0.15s ease;
}

.gs-fade-enter-active .gs-panel,
.gs-fade-leave-active .gs-panel {
    transition: transform 0.15s ease;
}

.gs-fade-enter-from,
.gs-fade-leave-to {
    opacity: 0;
}

.gs-fade-enter-from .gs-panel,
.gs-fade-leave-to .gs-panel {
    transform: scale(0.97) translateY(-6px);
}

@media (max-width: 575.98px) {
    .gs-backdrop {
        padding: 0.75rem;
    }

    .gs-panel {
        max-height: calc(100vh - 1.5rem);
    }
}
</style>

<style>
body.gs-no-scroll {
    overflow: hidden;
}
</style>
